import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function SportsBetting() {
  const { lang, currency, user, updateBalance } = useApp();
  const [matches, setMatches] = useState([]);
  const [myBets, setMyBets] = useState([]);
  const [tab, setTab] = useState("open");
  const [slip, setSlip] = useState(null);
  const [stake, setStake] = useState(currency==="HUF"?1000:5);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    const m = await api.get("/sports/matches").then(r=>r.data).catch(()=>[]);
    setMatches(m);
    const b = await api.get("/sports/my-bets").then(r=>r.data).catch(()=>[]);
    setMyBets(b);
  };

  useEffect(()=>{ refresh(); }, []);

  const pick = (m, side, odds) => {
    if (!odds) return;
    setSlip({match:m, pick:side, odds});
  };

  const placeBet = async () => {
    if (!slip) return;
    if (stake<=0) return toast.error("Invalid stake");
    setLoading(true);
    try {
      const r = await api.post("/sports/bet", { match_id: slip.match.id, pick: slip.pick, odds: slip.odds, stake: Number(stake), currency });
      updateBalance(currency, r.data.balance);
      toast.success(`Bet placed · ${fmt(r.data.potential, currency)} potential`);
      setSlip(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bet failed");
    } finally { setLoading(false); }
  };

  const pendingBets = myBets.filter(b=>b.status==="pending");

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <h1 className="font-serif text-4xl gold-text flex items-center gap-3"><Trophy/>{t(lang,"sports.title")}</h1>
        {user?.is_admin && <Link to="/admin/sports" data-testid="admin-sports-link" className="btn-ghost px-4 py-2 rounded-md text-xs uppercase tracking-widest">Admin · Settle</Link>}
      </div>
      <p className="text-white/40 mb-6 text-sm">Live mock odds (replaceable with The Odds API). Admin manually settles results.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-open" onClick={()=>setTab("open")} className={`px-4 py-2 rounded-md text-xs uppercase tracking-widest ${tab==="open"?"btn-gold":"btn-ghost"}`}>Open matches</button>
        <button data-testid="tab-mybets" onClick={()=>setTab("mybets")} className={`px-4 py-2 rounded-md text-xs uppercase tracking-widest ${tab==="mybets"?"btn-gold":"btn-ghost"}`}>My bets ({myBets.length})</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {tab==="open" && matches.map(m=>(
            <div key={m.id} className="lux-card p-5" data-testid={`match-${m.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">{m.league}</div>
              <div className="font-serif text-xl mt-1">{m.home} <span className="text-white/30 mx-2">vs</span> {m.away}</div>
              <div className="text-xs text-white/40 mt-1 flex items-center gap-1"><Clock size={12}/>{new Date(m.start).toLocaleString()}</div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <button data-testid={`bet-${m.id}-home`} onClick={()=>pick(m,"home",m.odds.home)} className="btn-ghost py-3 rounded-md text-sm">1 · {m.odds.home.toFixed(2)}</button>
                <button data-testid={`bet-${m.id}-draw`} disabled={!m.odds.draw} onClick={()=>pick(m,"draw",m.odds.draw)} className="btn-ghost py-3 rounded-md text-sm disabled:opacity-30">X · {m.odds.draw?m.odds.draw.toFixed(2):"-"}</button>
                <button data-testid={`bet-${m.id}-away`} onClick={()=>pick(m,"away",m.odds.away)} className="btn-ghost py-3 rounded-md text-sm">2 · {m.odds.away.toFixed(2)}</button>
              </div>
            </div>
          ))}
          {tab==="mybets" && myBets.length===0 && <div className="text-white/40 text-center py-12">No bets yet.</div>}
          {tab==="mybets" && myBets.map(b=>(
            <div key={b.bet_id} className="lux-card p-5" data-testid={`mybet-${b.bet_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">{b.league}</div>
                <div className="text-xs flex items-center gap-1">
                  {b.status==="pending" && <span className="text-yellow-400 flex items-center gap-1"><Clock size={12}/>PENDING</span>}
                  {b.status==="won" && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12}/>WON +{fmt(b.payout, b.currency)}</span>}
                  {b.status==="lost" && <span className="text-red-400 flex items-center gap-1"><XCircle size={12}/>LOST</span>}
                  {b.status==="void" && <span className="text-white/60">VOID (refunded)</span>}
                </div>
              </div>
              <div className="font-serif">{b.match_label}</div>
              <div className="text-sm text-white/70 mt-1">
                <span className="text-[#d4af37] uppercase mr-2">{b.pick}</span>
                @ {b.odds.toFixed(2)} · Stake {fmt(b.stake, b.currency)} · Potential {fmt(b.potential, b.currency)}
              </div>
            </div>
          ))}
        </div>

        <div className="lux-card p-6 h-fit sticky top-24">
          <h3 className="font-serif text-xl mb-4">Bet slip</h3>
          {!slip ? <div className="text-white/40 text-sm">Pick an outcome.</div> : (
            <>
              <div className="text-xs text-white/40">{slip.match.league}</div>
              <div className="font-serif mt-1">{slip.match.home} v {slip.match.away}</div>
              <div className="text-sm text-[#d4af37] mt-1">{slip.pick.toUpperCase()} @ {slip.odds.toFixed(2)}</div>
              <label className="text-xs uppercase tracking-widest text-white/50 mt-4 block">Stake ({currency})</label>
              <input data-testid="bet-stake" type="number" value={stake} onChange={e=>setStake(Number(e.target.value))} className="w-full mt-1 px-4 py-3 rounded-md bg-[#1a1a1a] border border-white/10 focus:border-[#d4af37] focus:outline-none"/>
              <div className="text-sm text-white/60 mt-3">Potential: <span className="text-[#d4af37]">{fmt(stake*slip.odds, currency)}</span></div>
              <button data-testid="place-bet" onClick={placeBet} disabled={loading} className="btn-gold w-full py-3 rounded-md mt-4">{loading?"…":t(lang,"sports.place_bet")}</button>
              <button onClick={()=>setSlip(null)} className="btn-ghost w-full py-2 rounded-md mt-2 text-xs">Cancel</button>
            </>
          )}
          {pendingBets.length>0 && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Pending {pendingBets.length}</div>
              {pendingBets.slice(0,3).map(b=>(
                <div key={b.bet_id} className="text-xs text-white/60 mb-1">{b.pick.toUpperCase()} @ {b.odds.toFixed(2)} · {fmt(b.stake, b.currency)}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
